import React, { memo, MouseEvent, useCallback, useMemo } from 'react'

import * as styles from './styles.module.scss'
import { CardProps } from './types'
import Image from '../image/image'
import Svg from '../svg/svg'
import { IconButton, Tooltip } from '..'

import classNames from 'classnames'
import { formatAmount } from '../../../utils/functions'

const Card = ({
   data,
   onCardClick,
   onDeleteClick,
   onViewItemsClick,
   isEmployeeUser,
   currentUserEmail,
}: CardProps) => {
   const isConnectionCard = data?.type === 'Connections'

   const isViewOnlyUserCard = isConnectionCard && data?.Role === 'ViewOnly'

   const title = useMemo(
      () =>
         isConnectionCard
            ? `${data.FirstName} ${data.LastName}`
            : data.Description,
      [data]
   )

   const handleCardEditClick = useCallback(
      (e: MouseEvent<HTMLElement>) =>
         onCardClick(isConnectionCard ? data.ID : data.Id, e),
      [onCardClick, data]
   )

   const handleViewItemsClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         const id = isConnectionCard ? data.ID : data.Id
         const label = isConnectionCard
            ? `${data.FirstName} ${data.LastName}`
            : data.Description
         onViewItemsClick(id, label)
      },
      [onViewItemsClick, data]
   )

   const handleDeleteClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         onDeleteClick(isConnectionCard ? data.ID : data.Id)
      },
      [onDeleteClick, data]
   )

   const isDeleteEnabled = useMemo(
      () =>
         isConnectionCard
            ? data?.Role !== 'Company' && data?.Email !== currentUserEmail
            : true,
      [isConnectionCard, data, currentUserEmail]
   )

   const hasYellowBorder = useMemo(
      () =>
         isConnectionCard
            ? data.Role === 'Admin' || data.Role === 'Company'
            : data.Warehouse,
      [data]
   )

   const roleOrStatus = useMemo(() => {
      if (isConnectionCard) {
         // prettify View Only role name
         if (data.Role === 'ViewOnly') return 'View Only'

         return data.Role
      }

      if (data.Archived) {
         return 'Archived'
      }

      if (data.Active) {
         return 'Active'
      }

      return 'Inactive'
   }, [data])

   const className = classNames(styles.card, {
      [styles.yellowBorder]: hasYellowBorder,
      [styles.blueBorder]: isConnectionCard && data.Role === 'Employee',
      [styles.lightBlueBorder]: isConnectionCard && data.Role === 'ViewOnly',
      [styles.archived]: !isConnectionCard && data.Archived,
   })

   return (
      <article
         className={className}
         role="button"
         tabIndex={0}
         aria-label={`Open ${title}`}
         onClick={handleCardEditClick}
      >
         <header>
            <div className={styles.imageAndTitle}>
               {isConnectionCard ? (
                  <Image
                     url={data?.PictureURL}
                     alt={`Picture of ${title}`}
                     width={22}
                     height={22}
                     isCircle
                  />
               ) : (
                  <Svg
                     id={data.Warehouse ? 'home-warehouse' : 'locations'}
                     width={data.Warehouse ? 18 : 16}
                     height={data.Warehouse ? 18 : 19}
                     overrideViewBox={
                        data.Warehouse ? '0 0 13 14' : '0 0 14.5 14'
                     }
                  />
               )}
               <h2>{title}</h2>
            </div>
            <div className={styles.iconsAndStatus}>
               <div className={styles.iconsContainer}>
                  {isConnectionCard && (
                     <>
                        {data?.AllowAddTool && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Add Tool">
                              <Svg id="add-item" />
                           </Tooltip>
                        )}
                        {!data?.RestrictItemDelete && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Delete Tool">
                              <Svg id="delete-item" />
                           </Tooltip>
                        )}
                        {data?.AllowAddLocation && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Add Location">
                              <Svg id="allow-add-location" />
                           </Tooltip>
                        )}
                        {data?.AllowTransferToEmployee && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Transfer To Employees">
                              <Svg id="allow-transfer-to-employees" />
                           </Tooltip>
                        )}
                        {data?.AllowToolTransferToVirtualConnections && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Item Transfer To Virtual Connections">
                              <Svg id="allow-transfer-item" />
                           </Tooltip>
                        )}
                        {data?.AllowToolTransferToViewOnlyConnections && !isViewOnlyUserCard && (
                           <Tooltip content="Allow Item Transfer To View Only Connections">
                              <Svg id="allow-transfer-to-view-only" />
                           </Tooltip>
                        )}
                        {data?.RequireScanToAccept && !isViewOnlyUserCard && (
                           <Tooltip content="Scan to Accept Required">
                              <Svg id="scan-item" />
                           </Tooltip>
                        )}
                        {data?.AutoAcceptToolAssignments && !isViewOnlyUserCard && (
                           <Tooltip content="Auto Accept Item Assignments">
                              <Svg id="auto-accept-item" />
                           </Tooltip>
                        )}
                        {data?.AllowDisplayAllInSearch && isViewOnlyUserCard && (
                           <Tooltip content="Allow View All Items In Search">
                              <Svg id="view-all-items" />
                           </Tooltip>
                        )}
                     </>
                  )}
               </div>
               <p>{roleOrStatus}</p>
            </div>
         </header>
         <section>
            <div className={styles.contentBox}>
               {isConnectionCard ? (
                  <>
                     <Svg id="home-warehouse" width={14} height={14} />
                     <p>
                        {formatAmount(
                           data?.HomeWarehouses?.length ?? 0,
                           'Home Warehouse'
                        )}
                     </p>
                  </>
               ) : (
                  <>
                     <Svg id="map" width={14} height={14} />
                     {data?.Address ? <p>{data.Address}</p> : <p>-</p>}
                  </>
               )}
            </div>
            {(isConnectionCard && data.Role !== 'Admin') ||
            !isConnectionCard ? (
               <button
                  type="button"
                  className={styles.contentBox}
                  onClick={handleViewItemsClick}
               >
                  <Svg
                     id="items"
                     width={14}
                     height={14}
                     fill="black"
                     stroke="black"
                  />
                  <p>
                     View{' '}
                     {(isConnectionCard && data.Role !== 'Company') ||
                     !isConnectionCard
                        ? 'Assigned'
                        : ''}{' '}
                     Items
                  </p>
                  <Svg id="expand" height={6} width={6} />
               </button>
            ) : (
               <div className={styles.contentBox} />
            )}
         </section>
         <footer>
            <p>{isConnectionCard ? data?.Title : ''}</p>
            {!isEmployeeUser && (
               <div className={styles.buttonsContainer}>
                  <Tooltip content="Edit">
                     <IconButton
                        id="edit-outlined"
                        action={handleCardEditClick}
                     />
                  </Tooltip>
                  {isDeleteEnabled && (
                     <Tooltip content="Delete">
                        <IconButton
                           id="delete-outlined"
                           action={handleDeleteClick}
                        />
                     </Tooltip>
                  )}
               </div>
            )}
         </footer>
      </article>
   )
}

export default memo(Card)
